import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { Link, Redirect } from "react-router-dom";
import {
  FaUserPlus,
  FaEye,
  FaSearch,
  FaPhone,
  FaEnvelope,
  FaSyncAlt,
  FaUsers,
  FaBuilding,
  FaArrowLeft,
  FaIdBadge,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

function staffInitials(name) {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

function KpiChip({ label, value, icon, tone = "blue" }) {
  return (
    <div className={`sm-kpi-chip sm-kpi-chip--${tone}`}>
      <span className="sm-kpi-chip__icon" aria-hidden>
        {icon}
      </span>
      <div className="sm-kpi-chip__text">
        <span className="sm-kpi-chip__label">{label}</span>
        <span className="sm-kpi-chip__value">{value}</span>
      </div>
    </div>
  );
}

const StaffAvatar = memo(function StaffAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imagePath]);

  const showImg = Boolean(imagePath) && !imgFailed;

  return (
    <div className="sl-row__avatar" aria-hidden>
      {showImg ? (
        <img
          src={imagePath}
          alt=""
          className="sl-row__img"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span className="sl-row__initials">{staffInitials(name)}</span>
      )}
    </div>
  );
});

const StaffRow = memo(function StaffRow({ staff, index }) {
  const staffId = staff.staff_id;
  if (!staffId) return null;

  const profilePath = `/AdminMain/StaffProfile/${encodeURIComponent(
    String(staffId)
  )}`;

  return (
    <article className="sl-row" style={{ "--sl-i": index }}>
      <span className="sl-row__index" aria-hidden>
        {String(index + 1).padStart(2, "0")}
      </span>

      <StaffAvatar name={staff.staff_name} imagePath={staff.staff_image_path} />

      <div className="sl-row__main min-w-0">
        <div className="sl-row__head">
          <h3 className="sl-row__name">{staff.staff_name || "Unnamed"}</h3>
          <div className="sl-row__chips">
            {staff.staff_role ? (
              <span className="sl-chip sl-chip--role">{staff.staff_role}</span>
            ) : null}
            {staff.login_type ? (
              <span className="sl-chip sl-chip--login">{staff.login_type}</span>
            ) : null}
            {staff.status ? (
              <span
                className={`sl-chip sl-chip--status${
                  String(staff.status).toLowerCase() === "active"
                    ? " is-active"
                    : ""
                }`}
              >
                {staff.status}
              </span>
            ) : null}
          </div>
        </div>

        <div className="sl-row__meta">
          <span className="sl-pill">
            <FaBuilding aria-hidden />
            {staff.department_name || "No department"}
          </span>
          {staff.staff_gender ? (
            <span className="sl-pill sl-pill--muted">{staff.staff_gender}</span>
          ) : null}
          {staff.staff_id ? (
            <span className="sl-pill sl-pill--id">
              <FaIdBadge aria-hidden />
              {staff.staff_id}
            </span>
          ) : null}
        </div>

        <div className="sl-row__contact">
          {staff.staff_phone ? (
            <span className="sl-pill sl-pill--contact">
              <FaPhone aria-hidden />
              {staff.staff_phone}
            </span>
          ) : null}
          {staff.staff_email ? (
            <span
              className="sl-pill sl-pill--contact"
              title={staff.staff_email}
            >
              <FaEnvelope aria-hidden />
              {staff.staff_email}
            </span>
          ) : null}
        </div>
      </div>

      <div className="sl-row__actions">
        <Link to={profilePath} className="sl-btn sl-btn--view">
          <FaEye aria-hidden />
          View profile
        </Link>
      </div>
    </article>
  );
});

const StaffList = () => {
  const loggedIn = localStorage.getItem("token") !== null;
  const [bootLoading, setBootLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchStaffList = useCallback(async ({ isRefresh = false } = {}) => {
    if (isRefresh) setRefreshing(true);
    else setBootLoading(true);

    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setStaffList([]);
      myToast.notify_warning("School code is missing.");
      setBootLoading(false);
      setRefreshing(false);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE()}getStaff.php`, {
        params: { school_code: schoolCode },
      });
      if (response.data.api_status === "OK") {
        setStaffList(response.data.staff || []);
      } else {
        setStaffList([]);
        myToast.notify_warning(
          response.data.api_remark || "Could not load staff list."
        );
      }
    } catch (error) {
      console.error("Error fetching staff list:", error);
      setStaffList([]);
      myToast.notify_warning("Network error loading staff list.");
    } finally {
      setBootLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchStaffList();
  }, [fetchStaffList]);

  const filteredStaff = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return staffList;
    return staffList.filter((s) => {
      const hay = [
        s.staff_name,
        s.staff_id,
        s.staff_role,
        s.login_type,
        s.department_name,
        s.staff_phone,
        s.staff_email,
        s.staff_gender,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [staffList, searchTerm]);

  const metrics = useMemo(() => {
    const withPhone = staffList.filter((s) => String(s.staff_phone || "").trim())
      .length;
    const withEmail = staffList.filter((s) => String(s.staff_email || "").trim())
      .length;
    const departments = new Set(
      staffList
        .map((s) => s.department_name)
        .filter((d) => d && String(d).trim())
    ).size;
    return [
      {
        label: "Total Staff",
        value: staffList.length.toLocaleString("en-IN"),
        icon: <FaUsers />,
        tone: "blue",
      },
      {
        label: "With Phone",
        value: withPhone.toLocaleString("en-IN"),
        icon: <FaPhone />,
        tone: "sky",
      },
      {
        label: "With Email",
        value: withEmail.toLocaleString("en-IN"),
        icon: <FaEnvelope />,
        tone: "mint",
      },
      {
        label: "Departments",
        value: departments.toLocaleString("en-IN"),
        icon: <FaBuilding />,
        tone: "sand",
      },
    ];
  }, [staffList]);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  if (bootLoading) {
    return <MyLoader />;
  }

  return (
    <div
      className="content payroll-page sm-page sl-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Staff List</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaUsers />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Staff Directory</h1>
              <p className="sm-hero__sub">
                View and manage all registered staff members
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <FaSearch className="sm-hero__search-icon" aria-hidden />
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search name, role, phone…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search staff"
              />
            </div>
            <Link to="/AdminMain/StaffManagement" className="sl-hero-link">
              <FaArrowLeft aria-hidden /> Staff Hub
            </Link>
            <button
              type="button"
              onClick={() => fetchStaffList({ isRefresh: true })}
              disabled={refreshing}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh list"
              aria-label="Refresh list"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <Link
              to="/AdminMain/StaffRegistration"
              className="sl-hero-link sl-hero-link--solid"
            >
              <FaUserPlus aria-hidden /> Add Staff
            </Link>
          </div>
        </header>

        <section className="sm-section" aria-label="Key metrics">
          <div className="sm-kpi-row">
            {metrics.map((m) => (
              <KpiChip
                key={m.label}
                label={m.label}
                value={m.value}
                icon={m.icon}
                tone={m.tone}
              />
            ))}
          </div>
        </section>

        <section className="sm-section sm-section--last sl-directory" aria-label="Staff list">
          <div className="sm-section-head">
            <h2 className="sm-section-head__title">Staff members</h2>
            <span className="sm-section-head__count">
              {filteredStaff.length}
              {searchTerm.trim() ? ` of ${staffList.length}` : ""} member
              {filteredStaff.length === 1 ? "" : "s"}
            </span>
          </div>

          {filteredStaff.length === 0 ? (
            <div className="sm-empty sl-empty">
              <div className="sl-empty__circle" aria-hidden>
                <FaSearch />
              </div>
              <p className="sm-empty__title">
                {staffList.length === 0
                  ? "No staff members found"
                  : "No matches for your search"}
              </p>
              <p className="sm-empty__hint">
                {staffList.length === 0
                  ? "Register your first staff member to see them listed here."
                  : "Try a different name, role, phone, or department."}
              </p>
              {staffList.length === 0 ? (
                <Link
                  to="/AdminMain/StaffRegistration"
                  className="sl-btn sl-btn--primary"
                  style={{ marginTop: 12 }}
                >
                  <FaUserPlus aria-hidden /> Register staff
                </Link>
              ) : null}
            </div>
          ) : (
            <div className="sl-list">
              {filteredStaff.map((staff, idx) => (
                <StaffRow
                  key={staff.staff_id ?? `staff-${idx}`}
                  staff={staff}
                  index={idx}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default StaffList;
